import React, { Component } from 'react'; // this import is required, otherwise we will get render issues (although it may seem unused)
import { Route } from 'react-router-dom';
import { Redirect } from 'react-router';
import utils from '../utils';
import RedirectHelper from '../redirectHelper';

/**
 * AuthorisedRoute component
 * This will either return an async route or a redirect, based on whether the user is authorised or not.
 * NeonRedirect will only trigger once the path is visited.
 * By default the authoriser action is "route". See config example below.
 * authoriser: {
 *     neon: {
 *         componentName: {
 *             route: ['role']
 *         }
 *     }
 * }
 *
 * @example
 * <AuthorisedRoute api={ api } path={ '/path' } component={ MyComponent } componentName="componentName" redirectPath="/login" ...rest />
 *
 * @param {API} api - api instance
 * @param {string} path - redirect path
 * @param {Component} component - component to return
 * @param {string} componentName - component name
 * @param {string} redirectPath - path to redirect when not authorised (optional)
 * @param {{}} rest - rest params
 * @returns {*} NeonRedirect or Route component
 */
export default class AuthorisedRoute extends Component {

    render() {
        const { api, path, componentName, redirectPath = '/' } = this.props;

        let redirect = redirectPath;

        if (!api) {
            throw new Error('<AuthorisedRoute> api property must be defined.');
        }

        if (!path) {
            throw new Error('<AuthorisedRoute> path property must be defined.');
        }

        if (!componentName) {
            throw new Error('<AuthorisedRoute> componentName property must be defined.');
        }

        // the authoriser will validate whether this is a user
        const authenticated = api.getAuthenticator().isAuthenticated();
        const authorised = api.getAuthoriser().authorise(api.getAuthenticator().getUser(), componentName, 'route');

        // forward all original props, except api (which we used here)
        const propsToForward = utils.excludeProps(['api', 'componentName'], this.props);

        // if not authenticated, redirect to login and set redirect path
        if (!authenticated) {
            RedirectHelper.instance.setRedirectPath(window.location.pathname);
            redirect = api.getAuthoriser().getLoginRedirect();
        }

        // if the user is logged in as  tenant user, but didn't select a tenant yet, to back to login flow
        const user = api.getAuthenticator().getUser();

        if (user && user.getIsTenantUser() && !user.getActiveOrganisationTenant()) {
            RedirectHelper.instance.setRedirectPath(window.location.pathname);
            redirect = api.getAuthoriser().getTenantSelectionRedirect();

            if (!redirect) {
                throw new Error('Invalid tenantSelectionRedirect provided in Authoriser');
            }
        }

        // create given root elements, so that the stack can be rendered
        // the children of this (Authorised) component are considered the root elements
        return authorised ? <Route { ...propsToForward } /> : <Redirect to={ redirect } />;
    }
}
